import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import '../ganttwidget.css';
import axios from "axios";
import { format, addDays, differenceInDays, startOfWeek, endOfWeek, addWeeks, differenceInWeeks, } from 'date-fns';
import {
    Switch, TextField, Button, Typography, Tooltip, IconButton, Modal,
    Box,
    CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const GanttWidget = () => {
    const [tasks, setTasks] = useState([]);
    const [newTask, setNewTask] = useState({ title: '', start_date: '', due_date: '' });
    const [hoveredTask, setHoveredTask] = useState(null);
    const [timeline, setTimeline] = useState({ topRow: [], bottomRow: [] });
    const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'weekly'
    const [taskPositions, setTaskPositions] = useState([]);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [widgetCode, setWidgetCode] = useState("");
    const calculateCellWidth = () => (viewMode === 'daily' ? 80 : 140);
    const [widgetDetails, setWidgetDetails] = useState(null);
    const [resultModalOpen, setResultModalOpen] = useState(false);

    const [containerHeight, setContainerHeight] = useState(0); // Dinamik yükseklik

    const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
    });

    // Modern Elegant Renk Paleti
    const colors = [
        'linear-gradient(135deg, #4b49ac, #6664EB)',
        'linear-gradient(135deg, #6D88B8, #98bdff)',
        'linear-gradient(135deg, #e9a1fb, #bad4ff)',
        'linear-gradient(135deg, #5E5DB5, #7978e9)',
        'linear-gradient(135deg, #6B89D6, #7da0fa)',

    ];

    // Rastgele bir renk seçen fonksiyon
    const getRandomColor = () => colors[Math.floor(Math.random() * colors.length)];

    useEffect(() => {
        if (tasks.length > 0) {
            const taskHeight = tasks.length * 33; // Her task için 33px yükseklik
            const timelineHeight = 50; // Timeline için sabit bir yükseklik
            let totalHeight = taskHeight + timelineHeight; // Toplam yükseklik hesaplama

            if (viewMode === 'weekly') {
                totalHeight += 10; // Weekly görünüm için ek yükseklik
            }
            else {
                totalHeight -= 20;
            }

            setContainerHeight(totalHeight); // Toplam yüksekliği ayarla
            console.log('Calculated height based on tasks and view mode:', totalHeight);
        }
    }, [tasks, viewMode]); // Tasks veya viewMode değiştiğinde yeniden hesapla


    // Yeni Görev Ekle
    const handleAddTask = () => {
        if (!newTask.title || !newTask.start_date || !newTask.due_date) {
            alert('Please fill in all fields');
            return;
        }

        const task = {
            id: tasks.length + 1,
            title: newTask.title,
            start_date: new Date(newTask.start_date),
            due_date: new Date(newTask.due_date),
            status: 'to-do',
            color: getRandomColor(),
        };

        setTasks([...tasks, task]);
        setNewTask({ title: '', start_date: '', due_date: '' });
    };


    // Minimum başlangıç tarihi
    const minDate = useMemo(() => {
        if (tasks.length === 0) return null;
        const minDate = new Date(Math.min(...tasks.map((task) => new Date(task.start_date))));
        return minDate;
    }, [tasks]);

    // Maksimum bitiş tarihi
    const maxDate = useMemo(() => {
        if (tasks.length === 0) return null;
        const maxDate = new Date(Math.max(...tasks.map((task) => new Date(task.due_date))));
        return maxDate;
    }, [tasks]);

    useEffect(() => {
        calculateTimeline();
        calculateTaskPositions();
    }, [tasks, viewMode]);

    const calculateTimeline = () => {
        if (!minDate || !maxDate) {
            setTimeline({ topRow: [], bottomRow: [] });
            return;
        }

        const timelineArray = { topRow: [], bottomRow: [] };

        if (viewMode === 'daily') {
            let currentDate = minDate;
            while (currentDate <= maxDate) {
                timelineArray.topRow.push(format(currentDate, 'MMM d'));
                currentDate = addDays(currentDate, 1);
            }
        } else if (viewMode === 'weekly') {
            const startWeek = startOfWeek(minDate, { weekStartsOn: 1 });
            const endWeek = endOfWeek(maxDate, { weekStartsOn: 1 });

            let currentWeek = startWeek;
            while (currentWeek <= endWeek) {
                const start = format(currentWeek, 'MMM d');
                const end = format(addDays(currentWeek, 6), 'MMM d');
                timelineArray.topRow.push(`${start} - ${end}`);
                timelineArray.bottomRow.push(['M', 'T', 'W', 'T', 'F', 'S', 'S']);
                currentWeek = addWeeks(currentWeek, 1);
            }
        }

        setTimeline(timelineArray);
    };

    const calculateTaskPositions = () => {
        const sortedTasks = [...tasks].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
        const positions = sortedTasks.map((task, index) => ({ ...task, top: index * 33 }));
        setTaskPositions(positions);
    };

    const getBarStyle = (start_date, due_date, status, color, top) => {
        const left =
            viewMode === 'daily'
                ? differenceInDays(new Date(start_date), minDate)
                : differenceInWeeks(
                    startOfWeek(new Date(start_date), { weekStartsOn: 1 }),
                    startOfWeek(minDate, { weekStartsOn: 1 })
                ) +
                (differenceInDays(new Date(start_date), startOfWeek(new Date(start_date), { weekStartsOn: 1 })) / 7); // Haftanın hangi gününden başladığını ekliyoruz

        const width =
            viewMode === 'daily'
                ? differenceInDays(new Date(due_date), new Date(start_date)) + 1
                : (differenceInDays(new Date(due_date), new Date(start_date)) + 1) / 7;

        return {
            left: `${left * calculateCellWidth()}px`,
            width: `${width * calculateCellWidth()}px`,
            top: `${top}px`,
            background: color, // Rastgele atanan rengi kullan,
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
        };
    };

    // Delete task
    const handleDeleteTask = (id) => {
        setTasks(tasks.filter((task) => task.id !== id));
    };

    const handleViewModeChange = () => {
        setViewMode((prevMode) => (prevMode === 'daily' ? 'weekly' : 'daily'));
    };

    const renderGridLines = () => {
        const totalCells = timeline.topRow.length; // Haftalık hücre sayısı
        const cellWidth = calculateCellWidth(); // Haftalık hücre genişliği
        const dailyWidth = cellWidth / 7; // Günlük çizgi genişliği

        const gridLines = [];

        for (let i = 0; i < totalCells; i++) {
            // Haftalık çizgiler
            gridLines.push(
                <div
                    key={`week-${i}`}
                    className="grid-line-w"
                    style={{
                        left: `${i * cellWidth}px`,
                        width: '1px',
                        height: '100%',
                        position: 'absolute',
                        backgroundColor: '#ddd', // Haftalık çizgi rengi
                        zIndex: 0,
                    }}
                />
            );

            // Günlük çizgiler (sadece görevler ve grid alanı için)
            if (viewMode === 'weekly') {
                for (let j = 1; j < 7; j++) {
                    gridLines.push(
                        <div
                            key={`day-${i}-${j}`}
                            className="grid-line-w daily"
                            style={{
                                left: `${i * cellWidth + j * dailyWidth}px`,
                                width: '1px',
                                height: 'calc(100% - 30px)', // Top Row'u hariç tut
                                position: 'absolute',
                                top: '30px', // Çizgiler, top row'un altından başlasın
                                backgroundColor: '#eee',
                                zIndex: 0,
                            }}
                        />
                    );
                }
            }
        }

        return gridLines;
    };

    // Open Email Modal
    const handleOpenEmailModal = () => {
        if (tasks.length === 0) {
            alert("Please add at least one task before creating your widget.");
            return;
        }
        setEmailModalOpen(true);
    };

    // Close Email Modal
    const handleCloseEmailModal = () => {
        setEmailModalOpen(false);
        setEmail("");
    };

    const handleCloseResultModal = () => {
        setResultModalOpen(false);
    };

    // Submit Gantt Chart
    const handleCreateWidget = async () => {
        if (!email) {
            alert("Please enter a valid email address.");
            return;
        }

        setLoading(true);

        try {
            const formData = {
                tasks,
                email,
            };

            const response = await api.post("/api/widget", formData);
            const { snippet } = response.data;

            setWidgetDetails(response.data);
            setEmailModalOpen(false);
            setResultModalOpen(true); // Sonuç modalını aç
        } catch (error) {
            console.error("Error creating widget:", error);
            alert("An error occurred while creating your widget.");
        } finally {
            setLoading(false);
        }
    };

    function decodeHTML(html) {
        const textArea = document.createElement("textarea");
        textArea.innerHTML = html;
        return textArea.value;
    }


    return (
        <div className="gantt-widget">
            <div style={{ padding: '20px' }}>
  <Typography variant="h5">Create Your Gantt Chart</Typography>
  <div className="form-container">
    <TextField
      label="Task Title"
      value={newTask.title}
      onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
      variant="outlined"
      size="small"
      className="form-field"
    />
    <TextField
      label="Start Date"
      type="date"
      value={newTask.start_date}
      onChange={(e) => setNewTask({ ...newTask, start_date: e.target.value })}
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
      className="form-field"
    />
    <TextField
      label="Due Date"
      type="date"
      value={newTask.due_date}
      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
      className="form-field"
    />
    <Button variant="contained" color="primary" onClick={handleAddTask} className="form-button">
      Add Task
    </Button>
  </div>

  <style>
    {`
      .form-container {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin: 20px 0;
        justify-content: space-between;
      }

      .form-field, .form-button {
        flex: 1;
        min-width: calc(33% - 10px);
        max-width: calc(33% - 10px);
      }

      @media (max-width: 768px) {
        .form-container {
          flex-direction: column;
        }

        
      }
    `}
  </style>
</div>



            <div className="view-switch-w" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {/* View Mode Switch */}
                <div style={{ display: 'flex', alignItems: 'center', marginLeft: '7px' }}>
                    <Typography variant="subtitle1">Daily</Typography>
                    <Switch checked={viewMode === 'weekly'} onChange={handleViewModeChange} />
                    <Typography variant="subtitle1">Weekly</Typography>
                </div>

                {/* Powered by GanttMaster */}
                <a
                    href="https://www.ganttmaster.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        fontFamily: 'Roboto, sans-serif',
                        fontWeight: 'bold',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        color: 'inherit',
                        marginLeft: '20px',
                        marginRight: '5px'
                    }}
                >
                    <span style={{ color: '#FFFFFF', marginRight: '5px' }}>Powered by</span>
                    <span style={{ color: '#FFFFFF' }}>GANTT</span>
                    <span style={{ color: '#807efe', marginLeft: '2px' }}>MASTER</span>
                </a>
            </div>

            <div className="timeline-tasks-container-w" style={{ position: 'relative', height: `${containerHeight}px` }}>
                {/* Grid Lines */}
                <div className="grid-lines-w">{renderGridLines()}</div>

                {/* Timeline */}
                <div className="timeline-w">
                    <div className="top-row-w">
                        {timeline.topRow?.map((date, index) => (
                            <div
                                key={index}
                                className="timeline-cell-w"
                                style={{ width: `${calculateCellWidth()}px` }}
                            >
                                {date}
                            </div>
                        ))}
                    </div>

                    {viewMode === 'weekly' && (
                        <div className="bottom-row-w">
                            {timeline.bottomRow?.map((days, index) =>
                                days?.map((day, i) => (
                                    <div key={`${index}-${i}`} className="timeline-cell-w">
                                        {day}
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>

                {/* Tasks */}
                <div className="tasks-w">
                    {taskPositions.map((task) => (
                        <Tooltip
                            key={task.id}
                            title={
                                <div>
                                    <Typography variant="subtitle2">{task.title}</Typography>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => handleDeleteTask(task.id)}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            }
                            arrow
                            placement="top"
                            onOpen={() => setHoveredTask(task.id)}
                            onClose={() => setHoveredTask(null)}
                        >
                            <div
                                className="task-bar-w"
                                style={getBarStyle(task.start_date, task.due_date, task.status, task.color, task.top)}
                            >
                                {task.title}
                            </div>
                        </Tooltip>
                    ))}
                </div>
            </div>

            {/* Create Widget Button */}
            <Button variant="contained" color="secondary" onClick={handleOpenEmailModal}>
                Create Gantt Chart for Your Website
            </Button>




            {/* Display Widget Code */}
            {widgetCode && (
                <div style={{ marginTop: "20px", padding: "10px", border: "1px solid #ddd" }}>
                    <Typography variant="h6">Your Widget Code:</Typography>
                    <pre style={{ backgroundColor: "#f4f4f4", padding: "10px", borderRadius: "5px" }}>
                        {widgetCode}
                    </pre>
                </div>
            )}
            {/* Email Modal */}
            <Modal open={emailModalOpen} onClose={handleCloseEmailModal}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: "8px",
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Enter Your Email Address
                    </Typography>
                    <TextField
                        fullWidth
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        variant="outlined"
                        size="small"
                    />
                    <div style={{ marginTop: "20px", display: "flex", justifyContent: "flex-end" }}>
                        <Button
                            variant="outlined"
                            style={{ marginRight: "10px" }}
                            onClick={handleCloseEmailModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCreateWidget}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : "Create Widget"}
                        </Button>
                    </div>
                </Box>
            </Modal>
            {/* Result Modal */}
            <Modal open={resultModalOpen} onClose={handleCloseResultModal}>
                <Box sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 500,
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    borderRadius: "8px",
                }}>
                    <Typography variant="h6" gutterBottom>
                        Your Gantt Chart is Ready!
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        Embed the widget on your website using the code below:
                    </Typography>
                    <div style={{ marginBottom: "10px", position: "relative" }}>
                        <pre style={{
                            background: "#f4f4f4",
                            padding: "10px",
                            borderRadius: "5px",
                            overflowX: "auto",
                            fontFamily: "monospace",
                        }}
                            dangerouslySetInnerHTML={{ __html: widgetDetails?.embedScript || "" }}
                        />
                        <div style={{ textAlign: "right", marginTop: "5px" }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => navigator.clipboard.writeText(decodeHTML(widgetDetails?.embedScript || ""))}
                            >
                                Copy Code
                            </Button>
                        </div>
                    </div>
                    <Typography variant="body2" style={{ marginTop: "10px" }}>
                        Or visit your widget: <a href={widgetDetails?.embedLink} target="_blank" rel="noopener noreferrer">{widgetDetails?.embedLink}</a>
                    </Typography>
                    <Typography variant="body1" style={{ marginTop: "20px", color: "#555" }}>
                        All details have been sent to your email address.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        style={{ marginTop: "20px" }}
                        onClick={handleCloseResultModal}
                    >
                        Close
                    </Button>
                </Box>
            </Modal>
            <div style={{
                backgroundColor: '#4C4C54',
                color: '#FFF',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                fontFamily: 'Roboto, sans-serif',
                paddingTop: '20px'
            }}>
                <h2 style={{
                    margin: '0 0 10px',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#807efe',
                }}>
                    How to Create Your Free Gantt Chart?
                </h2>
                <ol style={{
                    padding: '0 20px',
                    margin: '0',
                    textAlign: 'left',
                    lineHeight: '1.8',
                    fontSize: '16px',
                }}>
                    <li>
                        <strong>Add Your Tasks:</strong>
                        Create your tasks by entering titles and date ranges to plan your projects effectively.
                    </li>
                    <li>
                        <strong>Enter Your Email Address:</strong>
                        Provide your email address to finalize the widget creation process.
                    </li>
                    <li>
                        <strong>Embed Your Widget:</strong>
                        Copy the snippet code sent to your email and paste it into your website.
                    </li>
                </ol>
                <div style={{
                    marginTop: '15px',
                    fontSize: '14px',
                    color: '#aaa',
                    textAlign: 'center',
                }}>
                    Enjoy seamless Gantt chart integration on your website!
                </div>
            </div>
        </div>

    );
};

export default GanttWidget;
